/**
 * `id.ts` — pembangkit & validator **UUIDv7** (§3).
 *
 * ### Kenapa v7, bukan v4 atau composite
 * - **Terurut waktu** ⇒ sisipan tetap di ujung indeks (lokalitas); `key_range` sebuah batch sync
 *   jadi **bermakna** (rentang waktu), bukan rentang acak.
 * - **Tak butuh koordinasi antar-terminal** ⇒ bisa dibangkitkan di terminal saat luring (K3).
 * - Tak mewarisi kerapuhan `trankey = "1:100"` VTEC yang bertabrakan saat outlet direplikasi;
 *   keunikan composite bergantung pada tiga syarat yang harus benar selamanya di setiap mesin,
 *   dan sekali meleset `tranid` bentrok (kejadian nyata, SYNC §1).
 *
 * ### Kenapa di `contract`, bukan di tiap app
 * ID dibangkitkan **di terminal** (§3) tapi divalidasi portal. Dua implementasi berbeda untuk satu
 * format = penyimpangan tiga-lapis yang paket ini ada untuk mencegahnya.
 *
 * @module
 */
/**
 * Membangkitkan UUIDv7 (RFC 9562): 48 bit milidetik Unix + 4 bit versi + 12 bit acak +
 * 2 bit varian + 62 bit acak.
 *
 * @param now sumber waktu; disuntik agar uji deterministik.
 * @param randomBytes sumber acak; disuntik agar uji deterministik.
 */
export function uuidv7(now = Date.now, randomBytes = defaultRandomBytes) {
    const bytes = new Uint8Array(16);
    const ms = now();
    // 48 bit pertama = milidetik Unix, big-endian.
    bytes[0] = (ms / 2 ** 40) & 0xff;
    bytes[1] = (ms / 2 ** 32) & 0xff;
    bytes[2] = (ms / 2 ** 24) & 0xff;
    bytes[3] = (ms / 2 ** 16) & 0xff;
    bytes[4] = (ms / 2 ** 8) & 0xff;
    bytes[5] = ms & 0xff;
    const rnd = randomBytes(10);
    for (let i = 0; i < 10; i += 1)
        bytes[6 + i] = rnd[i] ?? 0;
    // Versi 7 di nibble tinggi byte ke-6; varian RFC (10xx) di dua bit tinggi byte ke-8.
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x70;
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
    const hex = [];
    for (let i = 0; i < 16; i += 1)
        hex.push((bytes[i] ?? 0).toString(16).padStart(2, '0'));
    const s = hex.join('');
    return `${s.slice(0, 8)}-${s.slice(8, 12)}-${s.slice(12, 16)}-${s.slice(16, 20)}-${s.slice(20)}`;
}
function defaultRandomBytes(n) {
    const out = new Uint8Array(n);
    // `globalThis.crypto` ada di Node ≥19 dan di browser — paket ini dipakai dua-duanya.
    const c = globalThis.crypto;
    if (c?.getRandomValues === undefined) {
        throw new Error('crypto.getRandomValues tidak tersedia — UUIDv7 tak boleh memakai Math.random');
    }
    c.getRandomValues(out);
    return out;
}
/** Milidetik Unix yang tertanam di sebuah UUIDv7. Berguna untuk retensi & forensik. */
export function uuidv7Timestamp(uuid) {
    const hex = uuid.replace(/-/g, '');
    if (hex.length !== 32)
        return undefined;
    const ms = Number.parseInt(hex.slice(0, 12), 16);
    return Number.isNaN(ms) ? undefined : ms;
}
//# sourceMappingURL=id.js.map